import { useCallback, useEffect, useRef, useState } from 'react';
import commonService from "@/services/common.service.ts";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import { CommonService } from "@/services";
import { htmlToText } from 'html-to-text';
import { useSocket } from '@/services/hooks/useSocket';
import { addNetworkError, clearNetworkErrors, NetworkError } from '@/store/actions/projectdata.action';

// Types for our error messages
export type ErrorMessage = {
    id: string;
    message: string;
    stack?: string | null;
    dom?: string | null;
    timestamp: string;
    type: string;
    method?: string;
    error_str?: string;
    error_id?: string;
    error_timestamp?: string;
};

export enum ErrorEventType {
    ERROR_TYPE = "preview_error",
}

// Type for incoming log data
interface LogData {
    err_source?: string;
    err_type: string;
    err_dom?: string | null;
    project_id?: string;
    err_timestamp: string;
    user_id?: string;
    stack?: string | null;
}

export const usePreviewErrors = () => {
    const [errors, setErrors] = useState<ErrorMessage[]>([]);
    const [isVisible, setIsVisible] = useState(false);
    const { projectId } = useParams<{ projectId: string }>();
    const currentUser = useSelector((state: IRootReducerState) => state.auth.user);
    const { technology: tech } = useParams<{ technology: string }>();
    const [aiConcurrencyIssue, setAiConcurrencyIssue] = useState(false);
    const [advancedModeIssue, setAdvancedModeIssue] = useState(false);
    const dispatch = useDispatch();

    // Use ref to track seen error IDs for O(1) duplicate detection
    const seenErrorIds = useRef<Set<string>>(new Set());

    // Maximum number of errors to keep in memory
    const MAX_ERRORS = 100;

    const handleSaveLogs = useCallback((log: LogData) => {
        // Generate unique error ID first
        const errorId = `${log.err_type}_${log.err_timestamp}_${log.err_dom || 'unknown'}`;

        // Fast duplicate check using Set
        if (seenErrorIds.current.has(errorId)) {
            return;
        }

        // Add to seen errors set
        seenErrorIds.current.add(errorId);

        // Convert content to text only once and only when needed
        const logContent = log.err_dom || log.err_type || '';
        const errorText = logContent ? htmlToText(logContent) : '';

        if (errorText.trim() === '') {
            return;
        }
        setIsVisible(true);

        // Update errors using functional update to avoid dependency on errors array
        setErrors(prevErrors => {
            const newError = {
                id: errorId,
                message: errorText,
                stack: log.stack,
                dom: log.err_dom,
                timestamp: log.err_timestamp,
                type: log.err_type,
                error_str: errorText,
                error_id: errorId,
                error_timestamp: log.err_timestamp,
            };

            const newErrors = [...prevErrors, newError];

            // Keep only the latest MAX_ERRORS to prevent memory issues
            if (newErrors.length > MAX_ERRORS) {
                const removedError = newErrors.shift();
                if (removedError?.error_id) {
                    seenErrorIds.current.delete(removedError.error_id);
                }
            }

            return newErrors;
        });
    }, []);

    // Function to fetch error logs from the API
    const fetchFlyLogErrors = useCallback(async () => {
        if (!projectId) return;

        try {
            commonService._project_service.getFlyLogErrors(projectId);
        } catch (error) {
            console.error('Failed to fetch fly log errors:', error);
        }
    }, [projectId]);

    // Combined message handler for both preview load and error messages
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const data = event.data;
            if (data && data?.source === "superengineer") {

                // Handle network errors separately - add to Redux only (don't show to user)
                if (data.type === 'network') {
                    const networkError: NetworkError = {
                        id: `${data.service}_${data.timestamp}_${data.url}`,
                        type: 'network',
                        service: data.service,
                        url: data.url,
                        method: data.method,
                        status: data.status,
                        error: data.error,
                        request: data.request,
                        response: data.response,
                        timestamp: data.timestamp
                    };
                    dispatch(addNetworkError(networkError));
                    // Don't set isVisible or show to user - just store in Redux for other logic
                    return;
                }

                // Handle other error messages (DOM, console, etc.)
                const errorMessage: LogData = {
                    err_source: tech ?? "nextjs",
                    err_type: data.type,
                    err_dom: data?.dom ?? data?.stack ?? null,
                    project_id: projectId,
                    err_timestamp: data.timestamp,
                    user_id: currentUser?.id
                };
                // Generate unique ID for this error
                handleSaveLogs(errorMessage);
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [projectId, tech, handleSaveLogs, currentUser, dispatch]);

    // Listen for RxJS trigger to fetch errors
    useEffect(() => {
        const sub = CommonService._communications.triggerErrorCardSubject.subscribe(() => {
            setErrors([]);
            // Fetch error logs when triggered
            fetchFlyLogErrors();
        });

        return () => sub.unsubscribe();
    }, [fetchFlyLogErrors]);

    // Construct a formatted error string for sharing
    const constructErrorString = useCallback((errorList: ErrorMessage[]): string => {
        return errorList.map((err: ErrorMessage) =>
            `${err.error_str}${err.stack ? `\nStack: ${err.stack}` : ''}`
        ).join('\n\n');
    }, []);

    // Clear all errors
    const clearErrors = useCallback(() => {
        setErrors([]);
        setIsVisible(false);
        // Clear the seen error IDs set to allow the same errors to be shown again if they occur
        seenErrorIds.current.clear();
        // Clear network errors from Redux
        dispatch(clearNetworkErrors());
    }, [dispatch]);

    const { events, clearEvent } = useSocket({
        eventTypes: [
            ErrorEventType.ERROR_TYPE,
        ],
        autoConnect: true,
        persist: true,
        projectId: projectId || "",
        logTag: 'PreviewRenderer',
        debug: false,
    });

    useEffect(() => {
        if (events[ErrorEventType.ERROR_TYPE]) {
            const errorData = events[ErrorEventType.ERROR_TYPE];

            // Only process errors with meaningful content
            if (errorData?.event_meta && errorData?.event_meta?.error_str) {
                // Create log object for socket errors
                const isSupabaseIssue = errorData?.event_meta?.extras?.supabase_connection_issue || false;
                const socketErrorLog = {
                    err_source: tech ?? "nextjs",
                    err_type: isSupabaseIssue ? "supabase_connection_issue" : "socket_error",
                    err_dom: errorData.event_meta.error_str || null,
                    supabase_error_issue: errorData.event_meta.extras?.supabase_connection_issue || false,
                    project_id: projectId,
                    err_timestamp: errorData.event_meta.error_timestamp,
                    user_id: currentUser?.id
                };

                handleSaveLogs(socketErrorLog);
            }
            clearEvent(ErrorEventType.ERROR_TYPE);
        }
    }, [events, handleSaveLogs, tech, projectId, currentUser, clearEvent]);

    // Listen for AI concurrency errors
    useEffect(() => {
        const sub = CommonService._communications.aiConcurrencyErrorSubject.subscribe(() => {
            setAiConcurrencyIssue(true);
        });

        return () => sub.unsubscribe();
    }, []);

    // Listen for advanced mode errors
    useEffect(() => {
        const sub = CommonService._communications.advancedModeErrorSubject.subscribe(() => {
            setAdvancedModeIssue(true);
        });

        return () => sub.unsubscribe();
    }, []);

    // Listen for Supabase connection success and clear Supabase errors
    useEffect(() => {
        const sub = CommonService._communications.supabaseConnectedSuccessSubject.subscribe(() => {
            // Remove all Supabase connection errors from the list
            setErrors(prevErrors => {
                const filteredErrors = prevErrors.filter(error => error.type !== 'supabase_connection_issue');

                // Also remove the error IDs from the seen set
                prevErrors.forEach(error => {
                    if (error.type === 'supabase_connection_issue' && error.error_id) {
                        seenErrorIds.current.delete(error.error_id);
                    }
                });

                // If no errors remain, hide the card
                if (filteredErrors.length === 0) {
                    setIsVisible(false);
                }

                return filteredErrors;
            });
        });

        return () => sub.unsubscribe();
    }, []);

    // Listen for preview start and clear all errors
    useEffect(() => {
        const sub = CommonService._communications.clearPreviewErrorsSubject.subscribe(() => {
            clearErrors();
            setAiConcurrencyIssue(false);
            setAdvancedModeIssue(false);
            dispatch(clearNetworkErrors());
        });

        return () => sub.unsubscribe();
    }, [clearErrors, dispatch]);

    // Clear network errors when project changes
    useEffect(() => {
        return () => {
            dispatch(clearNetworkErrors());
        };
    }, [projectId, dispatch]);

    // Clear network errors when user sends a new message
    useEffect(() => {
        const sub = CommonService._communications.sendMessageSubject.subscribe(() => {
            dispatch(clearNetworkErrors());
        });

        return () => sub.unsubscribe();
    }, [dispatch]);

    return {
        errors,
        setErrors, // In case we need to modify manually, though less common
        isVisible,
        setIsVisible,
        aiConcurrencyIssue,
        setAiConcurrencyIssue,
        advancedModeIssue,
        setAdvancedModeIssue,
        clearErrors,
        constructErrorString,
        projectId, // Exporting convenient values
    };
};
